import { useState, type CSSProperties, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router';
import { ArrowRight, Check, TriangleAlert } from 'lucide-react';
import { LEAVING_REASONS, type LeavingReason } from '@gaintplay/core/leaving';
import { api, coins, usd } from '../lib/api';
import { useCan } from '../lib/staff';
import { CoinMascot, Swatch } from '../components/Coin';
import { ErrorNote, statusLabel, TimeAgo } from '../components/ui';

interface SystemData {
  checks: { key: string; ok: boolean; note: string }[];
  wizzgift: { balance: number; currency: string } | null;
}
const useSystem = () =>
  useQuery({ queryKey: ['system'], queryFn: () => api<SystemData>('/system') });

interface OverviewData {
  users: number;
  signups24h: number;
  conversions24h: number;
  coins24h: number;
  revenueUsd24h: number;
  reversals24h: number;
  withdrawalsOpen: { status: string; n: number; usd: number }[];
  paid24h: { n: number; usd: number };
  ai24h: { reviews: number; costUsd: number };
  rejectedPostbacks24h: number;
  attention: {
    ticketsNeedStaff: number;
    openFlags: number;
    legacyNeedReview: number;
    legacyApprovals: number;
    legacyApprovalsVisible: boolean;
    withdrawalsOnHold: number;
    fraudAgreement: { reviewed: number; agreed: number };
  };
}

export function Stat({
  label,
  value,
  sub,
  money = false,
  to,
  warn = false,
}: {
  label: string;
  value: string;
  sub?: string;
  money?: boolean;
  to?: string;
  warn?: boolean;
}) {
  const body = (
    <>
      <div className="flex items-center gap-1 text-[13px] font-semibold text-stone">
        {label}
        {to && (
          <ArrowRight
            size={13}
            aria-hidden="true"
            className="ml-auto opacity-0 transition group-hover:translate-x-0.5 group-hover:opacity-100"
          />
        )}
      </div>
      <div
        className={`mt-2 text-[24px] font-extrabold leading-none tracking-[-0.025em] tabular-nums sm:text-[30px] ${warn ? 'text-berry-deep' : money ? 'text-coin-deep' : ''}`}
      >
        {value}
      </div>
      {sub && <div className="mt-2 text-[13px] leading-snug text-graphite">{sub}</div>}
    </>
  );
  const cls = `card block p-4 sm:p-5 ${warn ? '!border-berry/50' : ''}`;
  return to ? (
    <Link to={to} className={`${cls} group transition hover:-translate-y-0.5 hover:border-ink`}>
      {body}
    </Link>
  ) : (
    <div className={cls}>{body}</div>
  );
}

/** One "needs a person" queue: the count is the headline, zero reads as calm.
 *  Phones: a row (label + hint left, count right). Wider: a tile with the count in the middle. */
function Queue({ to, n, label, hint }: { to: string; n: number; label: string; hint: string }) {
  const hot = n > 0;
  return (
    <Link
      to={to}
      className={`group grid grid-cols-[minmax(0,1fr)_auto] items-center gap-x-3 rounded-[16px] border px-4 py-3 transition sm:flex sm:flex-col sm:items-stretch sm:p-4 ${
        hot
          ? 'border-white bg-white/[0.06] hover:bg-white/10'
          : 'border-white/15 hover:border-white/40'
      }`}
    >
      <div className="col-start-1 row-start-1 flex items-center gap-2 text-[13px] font-semibold text-white/70">
        {hot ? <Swatch color="coin" /> : <Check size={14} className="shrink-0 text-mint" />}
        <span className="truncate">{label}</span>
      </div>
      <div
        className={`col-start-2 row-span-2 row-start-1 text-[34px] font-extrabold leading-none tracking-[-0.03em] tabular-nums sm:mt-2 sm:text-[40px] ${hot ? 'text-coin' : 'text-white/35'}`}
      >
        {n}
      </div>
      <div className="col-start-1 row-start-2 mt-0.5 flex items-center gap-1 text-[13px] text-white/60 sm:mt-2">
        <span className="truncate">{hint}</span>
        <ArrowRight
          size={14}
          aria-hidden="true"
          className="ml-auto hidden shrink-0 transition group-hover:translate-x-0.5 sm:block"
        />
      </div>
    </Link>
  );
}

function Section({
  title,
  aside,
  children,
}: {
  title: string;
  aside?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section>
      <div className="mb-3 flex min-h-8 items-center gap-3">
        <h2 className="text-[20px] font-bold tracking-[-0.015em]">{title}</h2>
        {aside && <div className="ml-auto">{aside}</div>}
      </div>
      {children}
    </section>
  );
}

function SystemChecks() {
  const { data } = useSystem();
  if (!data) return null;
  const missing = data.checks.filter((c) => !c.ok);
  return (
    <Section
      title="Setup"
      aside={
        <span
          className={`inline-flex h-6 items-center rounded-full px-2.5 text-[12px] font-bold ${missing.length ? 'bg-coin-tint text-coin-ink' : 'bg-mint/15 text-mint-deep'}`}
        >
          {missing.length ? `${missing.length} to do` : 'All set'}
        </span>
      }
    >
      <ul className="card grid gap-x-6 p-2 md:grid-cols-2">
        {[...missing, ...data.checks.filter((c) => c.ok)].map((c) => {
          // Notes read "ENV_NAME + more: what it's for"; set the config names apart from the purpose.
          const [what, why] = c.note.includes(': ') ? c.note.split(/: (.*)/s) : [c.note, ''];
          return (
            <li
              key={c.key}
              className="flex items-start gap-3 rounded-[10px] px-3 py-2.5 text-[14px]"
            >
              <span
                className={`mt-0.5 grid h-5 w-5 shrink-0 place-items-center rounded-full ${c.ok ? 'bg-mint/15 text-mint-deep' : 'border-[1.5px] border-ink'}`}
              >
                {c.ok && <Check size={12} strokeWidth={3} />}
                <span className="sr-only">{c.ok ? 'Done:' : 'To do:'}</span>
              </span>
              <span className="min-w-0 leading-snug">
                <span
                  className={`break-words ${c.ok ? 'text-stone' : 'font-semibold'} ${why ? 'font-mono text-[13px]' : ''}`}
                >
                  {what}
                </span>
                {why && (
                  <span className={`block text-[13px] ${c.ok ? 'text-stone' : 'text-graphite'}`}>
                    {why.charAt(0).toUpperCase() + why.slice(1)}
                  </span>
                )}
              </span>
            </li>
          );
        })}
      </ul>
    </Section>
  );
}

/** One colour per open-withdrawal state, shared by the bar and its legend. */
const STATE_COLOR: Record<string, string> = {
  requested: 'bg-ink',
  risk_review: 'bg-graphite',
  kyc_pending: 'bg-sun',
  on_hold: 'bg-coin',
  approved: 'bg-stone',
  processing: 'bg-[#cfc8ba]',
};
const OTHER_COLORS = ['bg-ink/70', 'bg-graphite/60', 'bg-stone/60'];

export function Overview() {
  const { data, error, refetch } = useQuery({
    queryKey: ['overview'],
    queryFn: () => api<OverviewData>('/overview'),
    refetchInterval: 30_000,
  });
  const { data: system } = useSystem();
  if (!data)
    return error ? (
      <div className="space-y-4">
        <h1 className="page-title">Overview</h1>
        <ErrorNote error={error} onRetry={() => void refetch()} />
      </div>
    ) : (
      <div className="space-y-10" role="status" aria-label="Loading">
        <div className="h-[22rem] animate-pulse rounded-[24px] bg-ink/90 sm:h-64" />
        <div>
          <div className="mb-3 h-6 w-36 animate-pulse rounded bg-ink/[0.07]" />
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-3">
            {[0, 1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="card h-[120px] animate-pulse" />
            ))}
          </div>
        </div>
      </div>
    );
  const a = data.attention;
  const waiting =
    a.ticketsNeedStaff + a.withdrawalsOnHold + a.legacyNeedReview + a.legacyApprovals + a.openFlags;
  // Old-account tiles come and go: approvals while that section is up, first-rules holds while any wait.
  const showApprovals = a.legacyApprovalsVisible || a.legacyApprovals > 0;
  const showOldHolds = a.legacyNeedReview > 0 || !showApprovals;
  const tiles = 3 + Number(showApprovals) + Number(showOldHolds);
  const held = data.withdrawalsOpen.find((w) => w.status === 'on_hold');
  const openTotal = data.withdrawalsOpen.reduce((t, w) => t + w.usd, 0);
  const openCount = data.withdrawalsOpen.reduce((t, w) => t + w.n, 0);
  const wallet = system?.wizzgift ?? null;
  const short = wallet !== null && wallet.balance < openTotal;
  const colorOf = (status: string, i: number) =>
    STATE_COLOR[status] ?? OTHER_COLORS[i % OTHER_COLORS.length]!;

  return (
    <div className="space-y-10">
      {short && (
        <Link
          to="/giftcards"
          className="group flex items-start gap-3 rounded-[16px] border-[1.5px] border-berry bg-berry/[0.06] px-4 py-3.5 transition hover:bg-berry/[0.1] sm:px-5 sm:py-4"
        >
          <TriangleAlert size={20} className="mt-px shrink-0 text-berry-deep" aria-hidden="true" />
          <span className="min-w-0 flex-1 text-[14px] leading-relaxed">
            <b className="text-berry-deep">
              WizzGift balance is {usd(wallet.balance)}, less than the {usd(openTotal)} waiting to
              be paid.
            </b>{' '}
            Cash-outs fail when it runs out. Top up on wizzgift.com.
          </span>
          <ArrowRight
            size={16}
            aria-hidden="true"
            className="mt-1 hidden shrink-0 text-berry-deep transition group-hover:translate-x-0.5 sm:block"
          />
        </Link>
      )}
      {/* ---------- Is anything wrong? ---------- */}
      <section className="relative overflow-hidden rounded-[24px] bg-ink px-5 py-6 text-white sm:px-6 sm:py-7 md:px-8 md:py-8">
        <CoinMascot size={120} rotate={14} className="absolute -right-6 -top-8 hidden lg:block" />
        <div className="relative max-w-xl">
          <div className="flex items-center gap-2 text-[13px] font-semibold text-white/60">
            <Swatch color={waiting ? 'coin' : 'mint'} /> Overview
          </div>
          <h1 className="mt-2 text-[clamp(1.9rem,3.6vw,2.75rem)] font-extrabold leading-[1.02] tracking-[-0.03em]">
            {waiting ? (
              <>
                <span className="text-coin">{waiting}</span>{' '}
                {waiting === 1 ? 'thing needs' : 'things need'} a person.
              </>
            ) : (
              'Nothing needs you right now.'
            )}
          </h1>
          <p className="mt-2 text-[15px] leading-relaxed text-white/65">
            Claude handles the rest of the queue. These are the cases it handed to staff.
          </p>
        </div>
        <div
          className={`relative mt-6 grid gap-2 sm:mt-7 sm:grid-cols-2 sm:gap-3 ${tiles === 5 ? 'xl:grid-cols-5' : 'xl:grid-cols-4'}`}
        >
          <Queue
            to="/withdrawals"
            n={a.withdrawalsOnHold}
            label="Cash-outs on hold"
            hint={
              held?.n
                ? `${usd(held.usd)} waiting`
                : a.withdrawalsOnHold
                  ? 'Review now'
                  : 'Queue clear'
            }
          />
          <Queue
            to="/support"
            n={a.ticketsNeedStaff}
            label="Tickets for staff"
            hint={a.ticketsNeedStaff ? 'Reply in Support' : 'Inbox clear'}
          />
          <Queue
            to="/users?tab=flagged"
            n={a.openFlags}
            label="Open risk flags"
            hint={a.openFlags ? 'Confirm or dismiss' : 'None open'}
          />
          {showApprovals && (
            <Queue
              to="/legacy/approvals"
              n={a.legacyApprovals}
              label="Old accounts"
              hint={a.legacyApprovals ? 'Approve or reject' : 'None to approve'}
            />
          )}
          {showOldHolds && (
            <Queue
              to="/legacy?tab=review"
              n={a.legacyNeedReview}
              label="Old balances on hold"
              hint={a.legacyNeedReview ? 'Release or reject' : 'None waiting'}
            />
          )}
        </div>
        {a.fraudAgreement.reviewed > 0 && (
          <p className="relative mt-5 text-[13px] text-white/60">
            Staff agreed with Claude on {a.fraudAgreement.agreed} of {a.fraudAgreement.reviewed}{' '}
            flag decisions.
          </p>
        )}
      </section>

      {/* ---------- Last 24 hours ---------- */}
      <Section
        title="Last 24 hours"
        aside={<span className="text-[13px] text-stone">Refreshes every 30s</span>}
      >
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-3">
          <Stat label="Members" value={coins(data.users)} sub={`+${coins(data.signups24h)} new`} />
          <Stat
            label="Offers completed"
            value={coins(data.conversions24h)}
            sub={`${coins(data.coins24h)} coins · ${coins(data.reversals24h)} reversed`}
          />
          <Stat
            label="Partner revenue"
            value={usd(data.revenueUsd24h)}
            sub="As reported by networks"
            money
          />
          <Stat
            label="Paid out"
            value={usd(data.paid24h.usd)}
            sub={`${coins(data.paid24h.n)} cash-out${data.paid24h.n === 1 ? '' : 's'}`}
            money
            to="/withdrawals?tab=paid"
          />
          <Stat
            label="Claude reviews"
            value={coins(data.ai24h.reviews)}
            sub={`${usd(data.ai24h.costUsd)} model cost`}
            to="/decisions"
          />
          <Stat
            label="Refused postbacks"
            value={coins(data.rejectedPostbacks24h)}
            sub="Failed checks, paused network or unreadable"
            to="/postbacks?verified=false"
          />
        </div>
      </Section>

      {/* ---------- Open withdrawals ---------- */}
      <Section
        title="Open withdrawals"
        aside={
          <Link
            to="/withdrawals"
            className="inline-flex items-center gap-1 py-1 text-[14px] font-semibold text-ink underline decoration-coin decoration-2 underline-offset-4"
          >
            Open queue <ArrowRight size={14} aria-hidden="true" />
          </Link>
        }
      >
        {data.withdrawalsOpen.length === 0 ? (
          <div className="card flex items-center gap-3 px-5 py-5 text-[14px] text-stone">
            <Check size={16} className="text-mint-deep" aria-hidden="true" />
            No cash-outs in flight.
          </div>
        ) : (
          <div className="card overflow-hidden">
            <div className="flex h-2.5 gap-px bg-paper" aria-hidden="true">
              {data.withdrawalsOpen.map((w, i) => (
                <div
                  key={w.status}
                  title={`${statusLabel(w.status)}: ${usd(w.usd)}`}
                  style={{ flexGrow: Math.max(w.usd, 0.01) }}
                  className={colorOf(w.status, i)}
                />
              ))}
            </div>
            <div
              className="divide-y divide-hairline md:grid md:grid-cols-[repeat(var(--n),minmax(0,1fr))] md:divide-x md:divide-y-0"
              style={{ '--n': data.withdrawalsOpen.length } as CSSProperties}
            >
              {data.withdrawalsOpen.map((w, i) => (
                <Link
                  key={w.status}
                  to={`/withdrawals?tab=${w.status === 'on_hold' ? 'hold' : 'progress'}`}
                  className={`flex items-center justify-between gap-3 px-4 py-3 transition hover:bg-[#fbf8f3] sm:px-5 md:block md:py-4 ${w.status === 'on_hold' ? 'bg-coin-tint/30' : ''}`}
                >
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 text-[13px] font-semibold text-stone">
                      <span
                        aria-hidden="true"
                        className={`h-2.5 w-2.5 shrink-0 rounded-[3px] ${colorOf(w.status, i)}`}
                      />
                      <span className="truncate">{statusLabel(w.status)}</span>
                    </div>
                    <div className="mt-0.5 pl-[18px] text-[13px] text-graphite md:hidden">
                      {coins(w.n)} request{w.n === 1 ? '' : 's'}
                    </div>
                  </div>
                  <div className="shrink-0 text-right md:mt-1.5 md:text-left">
                    <div className="text-[20px] font-extrabold leading-tight tabular-nums text-coin-deep md:text-[22px]">
                      {usd(w.usd)}
                    </div>
                    <div className="hidden text-[13px] text-graphite md:block">
                      {coins(w.n)} request{w.n === 1 ? '' : 's'}
                    </div>
                  </div>
                </Link>
              ))}
            </div>
            <div className="flex flex-col gap-1 border-t border-hairline px-4 py-3 text-[13px] text-stone sm:flex-row sm:flex-wrap sm:items-center sm:gap-x-4 sm:px-5">
              <span>
                <b className="font-semibold text-ink tabular-nums">{usd(openTotal)}</b> in flight
                across {coins(openCount)} request{openCount === 1 ? '' : 's'}.
              </span>
              <Link to="/giftcards" className="hover:text-ink sm:ml-auto">
                WizzGift balance{' '}
                <b className={`tabular-nums ${short ? 'text-berry-deep' : 'text-ink'}`}>
                  {wallet ? usd(wallet.balance) : system ? 'unavailable' : '…'}
                </b>
                {short && <span className="text-berry-deep"> · not enough</span>}
              </Link>
            </div>
          </div>
        )}
      </Section>

      <WhyMembersLeft />

      <SystemChecks />
    </div>
  );
}

interface LeavingData {
  days: number;
  total: number;
  byReason: Partial<Record<LeavingReason, number>>;
  recent: {
    reasons: LeavingReason[];
    note: string | null;
    locale: string | null;
    country: string | null;
    accountAgeDays: number;
    cashedOut: boolean;
    coinsLeft: number;
    createdAt: number;
  }[];
}

/** What members picked on the web app's "Before you go" step (in their language there). */
const REASON_LABEL: Record<LeavingReason, string> = {
  earning_slow: 'Earning takes too long',
  missing_coins: 'Coins from offers didn’t arrive',
  cashout_hard: 'Cashing out too hard or slow',
  few_offers: 'Not enough offers in their country',
  surveys_screen_out: 'Surveys kept screening them out',
  privacy: 'Privacy or data worries',
  other_account: 'Has another account',
  not_using: 'Doesn’t use it anymore',
  other: 'Something else',
};

/**
 * Why members deleted their accounts, from the optional step before deleting (accounts in good
 * standing only). Anonymous: nothing links an answer to an account. Notes are user-written text.
 */
function WhyMembersLeft() {
  const can = useCan();
  const [days, setDays] = useState<30 | 90>(30);
  const { data, error, refetch } = useQuery({
    queryKey: ['deletion-feedback', days],
    queryFn: () => api<LeavingData>(`/deletion-feedback?days=${days}`),
    enabled: can('users.view'),
  });
  if (!can('users.view')) return null;
  const rows = data
    ? LEAVING_REASONS.map((r) => ({ r, n: data.byReason[r] ?? 0 }))
        .filter((x) => x.n > 0)
        .sort((x, y) => y.n - x.n)
    : [];
  const top = rows[0]?.n ?? 0;
  const notes = data?.recent.filter((f) => f.note).slice(0, 5) ?? [];
  return (
    <Section
      title="Why members left"
      aside={
        <div className="flex rounded-full border border-hairline bg-paper p-0.5 text-[13px] font-semibold">
          {([30, 90] as const).map((d) => (
            <button
              key={d}
              type="button"
              aria-pressed={days === d}
              onClick={() => setDays(d)}
              className={`min-h-8 rounded-full px-3 transition ${days === d ? 'bg-ink text-white' : 'text-graphite hover:text-ink'}`}
            >
              {d} days
            </button>
          ))}
        </div>
      }
    >
      {!data ? (
        error ? (
          <ErrorNote error={error} onRetry={() => void refetch()} />
        ) : (
          <div className="card h-40 animate-pulse" role="status" aria-label="Loading" />
        )
      ) : data.total === 0 ? (
        <div className="card px-5 py-5 text-[14px] text-stone">
          Nobody who deleted their account in the last {data.days} days said why. Members are asked
          on the last step before deleting, and can skip it.
        </div>
      ) : (
        <div className="card overflow-hidden">
          <div className="px-4 pt-4 text-[13px] text-stone sm:px-5">
            <b className="font-semibold text-ink tabular-nums">{coins(data.total)}</b> member
            {data.total === 1 ? '' : 's'} said why in the last {data.days} days. Each could pick
            more than one reason.
          </div>
          <ul className="space-y-2.5 px-4 py-4 sm:px-5">
            {rows.map(({ r, n }) => (
              <li
                key={r}
                className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-x-3 gap-y-1 sm:grid-cols-[minmax(0,15rem)_minmax(0,1fr)_3.5rem]"
              >
                <span className="truncate text-[14px] text-ink">{REASON_LABEL[r]}</span>
                <span className="col-span-2 row-start-2 h-2 overflow-hidden rounded-full bg-ink/[0.06] sm:col-span-1 sm:row-start-1 sm:col-start-2">
                  <span
                    className="block h-full rounded-full bg-coin"
                    style={{ width: `${Math.max(4, (n / top) * 100)}%` }}
                  />
                </span>
                <span className="text-right text-[14px] font-semibold tabular-nums text-ink">
                  {coins(n)}
                  <span className="ml-1 font-normal text-stone">
                    {Math.round((n / data.total) * 100)}%
                  </span>
                </span>
              </li>
            ))}
          </ul>
          {notes.length > 0 && (
            <div className="border-t border-hairline px-4 py-4 sm:px-5">
              <h3 className="text-[13px] font-semibold text-stone">Latest notes</h3>
              <ul className="mt-2 divide-y divide-hairline">
                {notes.map((f, i) => (
                  <li key={`${f.createdAt}-${i}`} className="py-2.5">
                    <p className="whitespace-pre-line text-[14px] leading-snug text-ink [overflow-wrap:anywhere]">
                      {f.note}
                    </p>
                    <p className="mt-1 text-[12px] text-stone tabular-nums">
                      <TimeAgo t={f.createdAt} />
                      {f.country && ` · ${f.country}`}
                      {f.locale && ` · ${f.locale}`}
                      {` · member ${coins(f.accountAgeDays)} day${f.accountAgeDays === 1 ? '' : 's'}`}
                      {f.cashedOut ? ' · had cashed out' : ' · never cashed out'}
                      {f.coinsLeft > 0 && ` · left ${coins(f.coinsLeft)} coins`}
                      {f.reasons.length > 0 &&
                        ` · ${f.reasons.map((r) => REASON_LABEL[r] ?? r).join(', ')}`}
                    </p>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </Section>
  );
}
